import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { listPageUrl, page_size } from 'api/apiUtils';
import { queryKeys } from 'api/queryKeys';
import { addRouteDestinations } from 'api/routes/routesApi';
import { getDestinationsList } from 'api/destinations/destinationsApi';
import { useListQuery } from 'api/useListQuery';
import { Destination } from 'components/common/ObjectTypes';
import {
  destinationsNeedAuthEnrichment,
  enrichDestinationsWithAuth,
} from 'components/destinations/DestinationHelperFunctions';
import { trackDestinationAdded } from 'analytics/analytics';
import type { RouteRecord } from './useRouteRecord';

type WorkspaceOption = { label: string; value: string };

// The Add Destination drawer: its search, workspace filter and page, the list
// they drive, and attaching the checked destinations to the Route.
export const useAddDestinationDrawer = (record: RouteRecord) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    getToken,
    routeId,
    route,
    routeProvider,
    destinations,
    defaultRouteId,
    pageLoaded,
  } = record;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  // The drawer's page — the same numbered pagination the Routes list uses.
  const [offset, setOffset] = useState(0);
  // Drawer workspace filter — defaults to the Route's workspace; clearing
  // it lists destinations from every workspace the user can see.
  const [workspaceFilter, setWorkspaceFilter] =
    useState<WorkspaceOption | null>(null);

  // The drawer refetches its list on open, but that effect runs after the first
  // paint — opening with the previous rows still in state flashed them for a
  // frame, and a click could land on a row about to be replaced by the fetch.
  // Opening through here shows the loading skeleton from the very first frame.
  // The drawer opens scoped to the Route's workspace with no search, set in the
  // same update as opening it so its first request already carries them.
  const open = () => {
    setWorkspaceFilter(
      route.owner?.id
        ? { label: route.owner.name, value: route.owner.id }
        : null,
    );
    setSearch('');
    setOffset(0);
    setIsOpen(true);
  };

  // Opening the drawer always fetches its list fresh (the cached copy is
  // stale by then); it waits for the Route so its workspace filter is set.
  const drawerQuery = useMemo(
    () => ({
      base_url__in: '',
      enabled: '',
      type__in: '',
      owner__in: workspaceFilter?.value ?? '',
      ordering: '',
      search,
      offset,
      previous: listPageUrl('/integrations/', offset - page_size),
      next: listPageUrl('/integrations/', offset + page_size),
    }),
    [workspaceFilter, search, offset],
  );
  const drawerList = useListQuery<Destination>(
    queryKeys.connections.drawer(drawerQuery),
    (token) => getDestinationsList(token, drawerQuery),
    isOpen && pageLoaded,
  );
  // EarthRanger rows in the drawer show which ER account they use (username or
  // truncated token) — the list endpoint omits configurations, so enrich them.
  // The plain rows show first; the enriched ones replace them when they land.
  const drawerAuth = useQuery({
    queryKey: queryKeys.connections.auth(drawerList.results.map((d) => d.id)),
    queryFn: () =>
      enrichDestinationsWithAuth(getToken() ?? '', drawerList.results),
    enabled:
      isOpen &&
      destinationsNeedAuthEnrichment(drawerList.results) &&
      !!getToken(),
  });
  // What's already attached to this Route can't be attached again, and the
  // Route's own provider (now listed as a destination too) would point the
  // Route at itself.
  const candidates = useMemo(() => {
    const attached = new Set([
      ...destinations.map((d) => d.id),
      routeProvider.id,
    ]);
    return (drawerAuth.data ?? drawerList.results).filter(
      (d) => !attached.has(d.id),
    );
  }, [drawerAuth.data, drawerList.results, destinations, routeProvider.id]);

  const handleSearch = (searchStr: string) => {
    setSearch(searchStr);
    setOffset(0);
  };

  const handleWorkspaceChange = (option: WorkspaceOption | null) => {
    setWorkspaceFilter(option);
    setOffset(0);
  };

  const handlePageClick = (e: any) => {
    setOffset(e.selected * page_size);
  };

  // Creating a destination for this Route opens the new-destination screen.
  const handleCreateNew = () => {
    setIsOpen(false);
    navigate(`/routes/${routeId}/configurations/newdestination`);
  };

  // Attach the checked destinations, then select the first one on the flow
  // map so its node is the panel's subject.
  const handleAttach = async (ids: string[]) => {
    if (ids.length === 0) return;
    const token = getToken();
    if (!token) return;
    const added = candidates.filter((d: Destination) => ids.includes(d.id));
    // Local name: this is the route RECORD id the PATCHes target. It is never
    // the page's :route_id URL param, which is the provider integration id.
    const recordId = defaultRouteId;
    if (!recordId) {
      console.log(
        'cannot attach destinations: route has no default route',
        route.id,
      );
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }
    const destination_ids = {
      destinations: Array.from(
        new Set([...destinations.map((d) => d.id), ...ids]),
      ),
    };
    try {
      const updated = await addRouteDestinations(
        token,
        recordId,
        destination_ids,
      );
      // A swallowed 401 resolves undefined — not an attach, so don't report one.
      if (!updated) return;
      added.forEach((d: Destination) =>
        trackDestinationAdded(route.id, d.type?.name, route.owner?.name),
      );
      toast.success(`${t('Route updated')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      setIsOpen(false);
      // The URL keeps the id the page is addressed by (same rule as the
      // remove path), so attaching never relocates the user mid-flow.
      navigate(`/routes/${routeId}`, {
        replace: true,
        state: { flowSelection: { kind: 'account', integrationId: ids[0] } },
      });
    } catch (error) {
      console.log(error);
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  };

  return {
    isOpen,
    open,
    close: () => setIsOpen(false),
    candidates,
    loading: drawerList.loading,
    totalCount: drawerList.count,
    offset,
    workspaceFilter,
    handleSearch,
    handleWorkspaceChange,
    handlePageClick,
    handleCreateNew,
    handleAttach,
  };
};
